import React, { useState, useEffect, useMemo } from 'react';
import { useStorefrontSettings } from '../../hooks/StorefrontSettingsContext';
import Storefront from '../../services/Storefront';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Loading from '../Loading';
import { GridToolbarContainer, GridToolbarFilterButton, GridToolbarExport, GridToolbarDensitySelector } from '@mui/x-data-grid-premium';
import Paper from '@mui/material/Paper';
import Notifications from '../../services/Notifications';
import { useStorefrontAdministrationConfig } from '../../hooks/StorefrontAdministrationConfigContext';
import CheckIcon from '@mui/icons-material/Check';
import { InputLabel, Select, MenuItem } from '@mui/material';
import TranslatableDataGrid from '../shared/TranslatableDataGrid';
import { ThemeProvider } from '@mui/material/styles';
import StorefrontAdminTheme from '../shared/MUIStyles/StorefrontAdminTheme';
import Typography from '@mui/material/Typography';
import { parse, format, isDate, isValid } from 'date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import MUIModal from '../shared/MUIModal';

export const DashboardComponentAdministration = () => {
    const storeData = useStorefrontSettings();
    const localized = useStorefrontAdministrationConfig().labels;
    const [fetching, setFetching] = useState(false);
    const [rows, setRows] = useState([]);
    const [value, setValue] = React.useState('');
    const [openUpdate, setOpenUpdate] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState(null);
    const [error, setError] = useState(false);
    const [helpText, setHelpText] = useState('');
    const theme = StorefrontAdminTheme;
    const responseMessages = {
        "GenericFailure": localized.UpdateDashboardSettingError,
        "UpdateSuccess": localized.UpdateDashboardSettingSuccess,
    };
    useEffect(() => {
        getDashboardComponents();
    }, []);

    const customToolbar = () => {
        return (
            <GridToolbarContainer>

                <Box sx={{ flexGrow: 1 }} />
                <GridToolbarFilterButton
                    slotProps={{
                        tooltip: { title: 'Filter list' }
                    }}
                    variant="outlined"
                    color="update"
                />
                <GridToolbarDensitySelector
                    slotProps={{
                        tooltip: { title: 'Change density' }
                    }}
                    variant="outlined"
                    color="update"
                />

                <GridToolbarExport
                    printOptions={{ disableToolbarButton: true }}
                    slotProps={{
                        tooltip: { title: 'Export data' }
                    }}
                    variant="outlined"
                    color="update"
                />
            </GridToolbarContainer>
        );
    }

    const getDashboardComponents = () => {
        setFetching(true);
        Storefront.getDashboardComponents(storeData.storefrontNumber).then(loaded);
    }

    const updateDashboardVisibility = async (form) => {
        var request = {
            storefrontNumber: storeData.storefrontNumber,
            id: form.id ?? null,
            value: form.isBoolean === '1' ? (form.value === 'true' ? 1 : 0) : form.value
        };
        return await Storefront.updateDashboardComponentVisibility(request).then((res) => {
            if (res) {
                const notificationType = res.response.endsWith("Success") ? "message" : "error";
                const message = responseMessages[res.response];

                if (message) {
                    Notifications[notificationType](message);
                }
                else {
                    Notifications.error(localized.UpdateDashboardSettingError);
                }
                getDashboardComponents();
            }
        });
    }

    const loaded = (data) => {
        if (data) {
            mapRows(data);
        }
        setFetching(false);
    }

    const mapRows = (data) => {
        var rows = [];
        data.dashboardComponentVisibilities.forEach((component) => {

            rows.push({
                id: component.componentId,
                settingName: component.settingName,
                value: component.value,
                isBoolean: component.isBoolean
            });
        });
        setRows(rows);
    }

    const handleClickOpenUpdate = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setError(false);
        setHelpText('');
        if (!row.isBoolean) { setValue(row.value); }
        else { setValue(row.value === '1' ? true : false); }
        setOpenUpdate(true);
    };

    const handleCloseUpdate = () => {
        setOpenUpdate(false);
    };

    const handleUpdateChange = (event) => {
        setValue(event.target.value);
    };
    const handleTimeChange = (updatedTime) => {
        if (isValid(updatedTime)) {
            setError(false);
            setHelpText('');
            setValue(updatedTime);
        } else {
            setError(true);
            setHelpText(localized.InvalidEasternTime);
        }
    };
    const convert12to24 = (time12h) => {
        const isDateFormat = isDate(time12h);
        if (!isDateFormat) {
            const parsedTime = parse(time12h, 'hh:mm a', new Date());
            const formatted = format(parsedTime, 'HH:mm');
            return formatted;
        }
        else {
            return format(time12h, 'HH:mm');
        }
    };
    const formatTime = (time) => {
        if (!error) {
            const convert24 = convert12to24(time);
            const parsedTime = parse(convert24, 'HH:mm', new Date());
            return parsedTime
        }
    }

    const renderUpdateButton = (row) => {
        return (
            <>
                <MUIModal
                    open={openUpdate}
                    onClose={handleCloseUpdate}
                    title={localized.enableDashboardComponents}
                    onSubmit={(event) => {
                        event.preventDefault();
                        const formData = new FormData(event.currentTarget);
                        formData.append("id", row.id);
                        formData.append("isBoolean", row.isBoolean ? '1' : '0');
                        const formJson = Object.fromEntries(formData.entries());
                        if (!row.isBoolean) {
                            formJson.value = format(value, 'hh:mm a');
                        }
                        updateDashboardVisibility(formJson, "update");
                        handleCloseUpdate();
                    }}

                    showDefaultButtons={true}
                    cancelLabel={localized.StorefrontAdminCancel}
                    submitLabel={localized.StorefrontAdminUpdate}
                    onCancel={handleCloseUpdate}

                    submitButtonProps={{ color: "update" }}
                    cancelButtonProps={{ color: "error" }}
                    maxWidth="sm"
                    fullWidth
                >
                    <h3>{localized.ValuesEligibleForUpdateBelow}</h3>

                    {row.isBoolean ? (
                        <>
                            <InputLabel id="value-label">{localized.IsEnabled}</InputLabel>
                            <Select
                                required
                                fullWidth
                                labelId="value-label"
                                id="value"
                                name="value"
                                value={value}
                                label={localized.IsEnabled}
                                onChange={(x) => handleUpdateChange(x)}
                                MenuProps={{
                                    disablePortal: true,
                                    PaperProps: {
                                        sx: { zIndex: 1600 },
                                    },
                                }}
                            >
                                <MenuItem value={true}>{localized.True}</MenuItem>
                                <MenuItem value={false}>{localized.False}</MenuItem>
                            </Select>

                        </>
                    ) : (
                        <>
                            <InputLabel id="time-label" required>{localized.EasternTimeValue}</InputLabel>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DemoContainer components={['TimePicker']}>
                                    <TimePicker
                                        value={formatTime(value)}
                                        onChange={handleTimeChange}
                                            error={error}

                                        slotProps={{
                                            textField: {
                                                error: error,
                                                helperText: helpText,
                                                sx: {
                                                    height: 50,
                                                    '& .MuiInputBase-root': {
                                                        height: 30,
                                                    },
                                                    '& input': {
                                                        padding: '15px 25px',
                                                        fontSize: 15,
                                                        overflow: 'hidden',
                                                        height: 30,
                                                    },
                                                    '& .MuiFormHelperText-root': {
                                                        color: error ? 'red' : 'gray',
                                                        fontStyle: 'italic',
                                                        overflow: 'hidden',
                                                        height: 40,
                                                    },
                                                },
                                            },
                                            popper: {
                                                modifiers: [
                                                    {
                                                        name: 'zIndex',
                                                        enabled: true,
                                                        phase: 'write',
                                                        fn: ({ state }) => {
                                                            state.styles.popper.zIndex = 1600;
                                                        },
                                                    },
                                                ],
                                            },
                                            }}
                                            disablePortal
                                    />
                                </DemoContainer>
                            </LocalizationProvider>
                        </>
                    )}
                </MUIModal>
            </>
        );
    };


    //const renderUpdateButton = (row) => {
    //    return (
    //        <>
    //            <Dialog
    //                open={openUpdate}
    //                onClose={handleCloseUpdate}
    //                PaperProps={{
    //                    component: 'form',
    //                    onSubmit: (event) => {
    //                        event.preventDefault();
    //                        const formData = new FormData(event.currentTarget);
    //                        formData.append("id", row.id);
    //                        formData.append("isBoolean", row.isBoolean ? '1' : '0');
    //                        const formJson = Object.fromEntries(formData.entries());
    //                        if (!row.isBoolean) {
    //                            formJson.value = format(value, 'hh:mm a');
    //                        }
    //                        updateDashboardVisibility(formJson, "update");
    //                        handleCloseUpdate();
    //                    },
    //                }}
    //            >
    //                <DialogTitle><h1>{localized.enableDashboardComponents}</h1></DialogTitle>
    //                <DialogContent>
    //                    <DialogContentText>
    //                        <h3>{localized.ValuesEligibleForUpdateBelow}</h3>
    //                    </DialogContentText>
    //                    {row.isBoolean ?
    //                        (<><InputLabel id="value-label">{localized.IsEnabled}</InputLabel>
    //                            <Select
    //                                required
    //                                fullWidth
    //                                labelId="value-label"
    //                                id="value"
    //                                name="value"
    //                                value={value}
    //                                label={localized.IsEnabled}
    //                                onChange={(x) => handleUpdateChange(x)}
    //                            >
    //                                <MenuItem value={true}>{localized.True}</MenuItem>
    //                                <MenuItem value={false}>{localized.False}</MenuItem>
    //                            </Select></>) : (
    //                            <>  <InputLabel id="time-label" required>{localized.EasternTimeValue}</InputLabel>
    //                                <LocalizationProvider dateAdapter={AdapterDateFns}>
    //                                    <DemoContainer components={['TimePicker']}>
    //                                        <TimePicker value={formatTime(value)} onChange={handleTimeChange} error={error}
    //                                            slotProps={{
    //                                                textField: {
    //                                                    error: error,
    //                                                    helperText: helpText,
    //                                                    sx: {
    //                                                        height: 50,
    //                                                        '& .MuiInputBase-root': {
    //                                                            height: 30,
    //                                                        },
    //                                                        '& input': {
    //                                                            padding: '15px 25px',
    //                                                            fontSize: 15,
    //                                                            overFlow: 'hidden',
    //                                                            height: 30
    //                                                        },
    //                                                        '& .MuiFormHelperText-root': {
    //                                                            color: error ? 'red' : 'gray',
    //                                                            fontStyle: 'italic',
    //                                                            overflow: 'hidden',
    //                                                            height: 40,
    //                                                        },
    //                                                    },

    //                                                },
    //                                            }} />
    //                                    </DemoContainer>
    //                                </LocalizationProvider>

    //                            </>
    //                        )}
    //                </DialogContent>
    //                <DialogActions>
    //                    <Button variant="standard" color="error" onClick={handleCloseUpdate}>{localized.StorefrontAdminCancel}</Button>
    //                    <Button variant="standard" color="update" type="submit" disabled={error}>{localized.StorefrontAdminUpdate}</Button>
    //                </DialogActions>
    //            </Dialog>
    //        </>
    //    )
    //}

    const columns = [
        {
            field: 'settingName',
            headerName: localized.DashboardSettingName,
            headerAlign: 'center',
            align: 'center',
            flex: 1
        },
        {
            field: 'value',
            headerName: 'Value',
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            renderCell: (params) => {
                return (
                    <strong>
                        {params.row.isBoolean ? (params.value === '1' ? <CheckIcon style={{ color: "rgb(56, 178, 95)" }} /> : null) : params.value}
                    </strong>
                )
            }
        },
        {
            field: 'updateButton',
            headerName: localized.UpdateDashboardWidget,
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <strong>
                        <Button
                            variant="standard"
                            color="update"
                            style={{ marginLeft: 16 }}
                            onClick={(e) => handleClickOpenUpdate(e, params.row)}
                        >
                            {localized.UpdateDashboardWidget}
                        </Button>
                    </strong>
                )
            },
            width: 300,
            disableClickEventBubbling: true

        }
    ];
    const dashboardItems = useMemo(() => {
        return (
            <Paper sx={{ height: 700, width: '100%' }}>
                <TranslatableDataGrid rows={rows} columns={columns} insetToolbar={customToolbar} labels={localized} />
            </Paper>
        );
    }, [rows, localized]);
    return (
        <>
            {
                fetching && <Loading type="brand" />
            }
            {
                !fetching && (localized && Object.keys(localized).length > 0) &&
                <>
                    <ThemeProvider theme={theme}>
                        <Typography variant="h1" >{localized.DashboardWidgetAdministrationHeader}</Typography>
                        {dashboardItems}
                        {openUpdate && renderUpdateButton(selectedRow)}
                    </ThemeProvider>
                </>
            }
        </>
    );
}

export default DashboardComponentAdministration;

